import { ClipboardEvent, RefObject } from 'react';
import { extractInsightMentionText } from '../insight-clipboard-marker';
import {
  buildFragmentFromClipboardText,
  serializeSelection,
} from './clipboard';

export interface UseMentionClipboardArgs {
  editorRef: RefObject<HTMLDivElement | null>;
  disabled?: boolean;
  recompute: () => void;
  clearChipSelection: () => void;
}

export interface UseMentionClipboardResult {
  onCopy: (e: ClipboardEvent<HTMLDivElement>) => void;
  onCut: (e: ClipboardEvent<HTMLDivElement>) => void;
  onPaste: (e: ClipboardEvent<HTMLDivElement>) => void;
}

/** A pasted insight collapses to its `@[title](insight:id)` marker; everything else pastes as plain text. */
const resolvePastedText = (e: ClipboardEvent<HTMLDivElement>): string => {
  const html = e.clipboardData.getData('text/html');
  return (
    (html && extractInsightMentionText(html)) ||
    e.clipboardData.getData('text/plain')
  );
};

export const useMentionClipboard = ({
  editorRef,
  disabled,
  recompute,
  clearChipSelection,
}: UseMentionClipboardArgs): UseMentionClipboardResult => {
  const onCopy = (e: ClipboardEvent<HTMLDivElement>) => {
    const root = editorRef.current;
    if (!root) {
      return;
    }
    const text = serializeSelection(root);
    if (text === null) {
      return;
    }
    e.preventDefault();
    e.clipboardData.setData('text/plain', text);
  };

  const onCut = (e: ClipboardEvent<HTMLDivElement>) => {
    const root = editorRef.current;
    if (!root) {
      return;
    }
    const text = serializeSelection(root);
    if (text === null) {
      return;
    }
    e.preventDefault();
    e.clipboardData.setData('text/plain', text);

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }
    selection.getRangeAt(0).deleteContents();
    root.normalize();
    clearChipSelection();
    recompute();
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const root = editorRef.current;
    if (!root || disabled) {
      return;
    }

    e.preventDefault();
    const text = resolvePastedText(e);
    if (!text) {
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) {
      return;
    }

    range.deleteContents();
    const existingMentionCount = root.querySelectorAll(
      '[data-mention-chip]'
    ).length;
    const { fragment, lastNode } = buildFragmentFromClipboardText(
      text,
      existingMentionCount
    );
    range.insertNode(fragment);
    root.normalize();

    if (lastNode) {
      const after = document.createRange();
      if (lastNode.nodeType === Node.TEXT_NODE) {
        after.setStart(lastNode, lastNode.nodeValue?.length ?? 0);
      } else {
        after.setStartAfter(lastNode);
      }
      after.collapse(true);
      selection.removeAllRanges();
      selection.addRange(after);
    }

    clearChipSelection();
    recompute();
  };

  return { onCopy, onCut, onPaste };
};
